import * as React from 'react'
import { cn } from '@/lib/cn'

/**
 * Chart primitives for the System view.
 *
 * ── The colour decisions, and why there is no palette here ──────────
 * Every chart on this page is a SINGLE series, so none of them needs a
 * categorical palette: the data marks wear the accent, the trend context wears
 * the de-emphasis grey, and the only other colours in play are the console's
 * reserved status tokens on the meters. That is deliberate rather than
 * incidental — CPU and event-loop delay are two measures on wildly different
 * scales, and the one thing they must never become is a two-axis chart, whose
 * arbitrary scale alignment invents a correlation that is not in the data. They
 * are two charts side by side instead.
 *
 * Indigo carries data here even though the console's rule is "accent is never a
 * status". The rule is about chrome — a selected row, a focused input — and on a
 * plot the accent is the only ink that is *supposed* to be loud. Status tokens
 * stay reserved for the meters, where a colour genuinely means good or bad.
 *
 * ── Specs the marks follow ──────────────────────────────────────────
 * 2px lines with round joins, a ~10% area wash, end markers at least 8px
 * carrying a 2px ring in the surface colour so they stay legible where they
 * cross the line, hairline solid gridlines one step off the surface (never
 * dashed — dashing reads as a threshold), and a value on the ENDPOINT only.
 * A number on every point is chaos and goes unread.
 */

export interface ChartPoint {
  /** Epoch ms. Callers normalise — Railway reports seconds, self-metrics ms. */
  x: number
  y: number
}

/* ------------------------------------------------------------------ *
 * Measuring
 * ------------------------------------------------------------------ */

/**
 * The rendered width of an element.
 *
 * A crosshair has to convert a pointer's pixel position into a data index, so
 * the chart needs real pixels rather than a viewBox with a scaling transform.
 * The alternative — `preserveAspectRatio="none"` — would also stretch the
 * strokes, giving a 2px line that is 2px tall and 5px wide.
 */
function useElementWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = React.useState(0)

  React.useEffect(() => {
    const node = ref.current
    if (!node || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setWidth(entry.contentRect.width)
    })
    observer.observe(node)
    setWidth(node.getBoundingClientRect().width)

    return () => {
      observer.disconnect()
    }
  }, [])

  return [ref, width]
}

/* ------------------------------------------------------------------ *
 * Scales
 * ------------------------------------------------------------------ */

/**
 * A round number at or above `value`, for the top gridline.
 *
 * Axis ticks land on 1/2/2.5/5/10 × a power of ten so the labels read as
 * quantities rather than as whatever the maximum sample happened to be.
 */
function niceCeiling(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalised = value / magnitude
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 2.5 ? 2.5 : normalised <= 5 ? 5 : 10
  return step * magnitude
}

/* ------------------------------------------------------------------ *
 * Sparkline
 * ------------------------------------------------------------------ */

/**
 * Trend context inside a stat tile. No axes, no labels, no hover.
 *
 * The line is the de-emphasis grey and only the newest point wears the accent:
 * on a tile the number IS the message and the shape behind it is there to say
 * whether the number is going anywhere. A fully accented sparkline competes
 * with the value it is supposed to support.
 */
export function Sparkline({
  points,
  className,
  height = 28,
}: {
  points: readonly ChartPoint[]
  className?: string
  /** Shorter in the rail, where the chip has a row to spare and not much more. */
  height?: number
}) {
  const [ref, width] = useElementWidth()

  const path = React.useMemo(() => {
    if (width <= 0 || points.length < 2) return null

    let min = Infinity
    let max = -Infinity
    for (const p of points) {
      if (p.y < min) min = p.y
      if (p.y > max) max = p.y
    }
    // A flat series has no range to scale against; centring it keeps the line
    // horizontal instead of dividing by zero and drawing nothing.
    const span = max - min || 1
    const pad = 3

    const step = width / (points.length - 1)
    const coords = points.map((p, i) => {
      const x = i * step
      const y = height - pad - ((p.y - min) / span) * (height - pad * 2)
      return { x, y }
    })

    return {
      d: coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(' '),
      last: coords[coords.length - 1],
    }
  }, [points, width, height])

  return (
    <div ref={ref} className={cn('w-full', className)} style={{ height }} aria-hidden="true">
      {path ? (
        <svg width={width} height={height} className="overflow-visible">
          <path
            d={path.d}
            fill="none"
            className="stroke-line-strong"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {path.last ? (
            <circle
              cx={path.last.x}
              cy={path.last.y}
              r={2.5}
              className="fill-accent stroke-s1"
              strokeWidth={2}
            />
          ) : null}
        </svg>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Meter
 * ------------------------------------------------------------------ */

export type MeterTone = 'accent' | 'warn' | 'danger'

const METER_FILL: Record<MeterTone, string> = {
  accent: 'bg-accent',
  warn: 'bg-warn',
  danger: 'bg-danger',
}

/**
 * The unfilled track is a lighter step of the SAME ramp as the fill, so the
 * state reads across the whole bar rather than only across the filled part.
 */
const METER_TRACK: Record<MeterTone, string> = {
  accent: 'bg-accent-soft',
  warn: 'bg-warn-soft',
  danger: 'bg-danger-soft',
}

/**
 * One ratio against a limit. A meter rather than a two-slice pie, and rather
 * than a one-bar bar chart — the number is the chart, and the bar only says how
 * much headroom is left.
 */
export function Meter({
  value,
  limit,
  tone = 'accent',
  label,
}: {
  value: number
  /** Null renders an empty track: Railway reports no limit for some services. */
  limit: number | null
  tone?: MeterTone
  label: string
}) {
  const ratio = limit && limit > 0 ? Math.min(1, Math.max(0, value / limit)) : null

  return (
    <div
      className={cn('h-1.5 w-full overflow-hidden rounded-full', METER_TRACK[tone])}
      role="meter"
      aria-valuenow={ratio === null ? undefined : Math.round(ratio * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      {ratio === null ? null : (
        <div
          className={cn('h-full rounded-full transition-[width] duration-500', METER_FILL[tone])}
          /*
           * A floor of 2px on a non-zero ratio. Real usage here is often well
           * under 5% of a generous plan limit, and a bar rounded to zero pixels
           * is indistinguishable from a service that is doing nothing at all.
           */
          style={{ width: ratio === 0 ? 0 : `max(2px, ${(ratio * 100).toFixed(2)}%)` }}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Line chart
 * ------------------------------------------------------------------ */

/** Room for the y labels, the endpoint value, and the x-axis band. */
const PAD = { top: 10, right: 52, bottom: 20, left: 46 }

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

export function LineChart({
  points,
  title,
  formatValue,
  height = 132,
  emptyLabel = 'No samples yet.',
}: {
  points: readonly ChartPoint[]
  /** Names the single series, which is why there is no legend box. */
  title: string
  formatValue: (value: number) => string
  /** PLOT height. The band for the x labels is added on top of it, so the
      axis can never be the thing that overflows a fixed container. */
  height?: number
  emptyLabel?: string
}) {
  const [ref, width] = useElementWidth()
  const [hover, setHover] = React.useState<number | null>(null)

  const plotWidth = Math.max(0, width - PAD.left - PAD.right)
  const totalHeight = height + PAD.top + PAD.bottom

  const model = React.useMemo(() => {
    if (plotWidth <= 0 || points.length === 0) return null

    let max = 0
    for (const p of points) if (p.y > max) max = p.y
    // Headroom above the peak so the line never runs along the top edge, and a
    // floor so an all-zero series still gets a sane axis instead of collapsing.
    const top = niceCeiling(Math.max(max * 1.15, Number.EPSILON))

    const step = points.length > 1 ? plotWidth / (points.length - 1) : 0
    const coords = points.map((p, i) => ({
      x: PAD.left + (points.length > 1 ? i * step : plotWidth / 2),
      y: PAD.top + height - (p.y / top) * height,
    }))

    const line = coords
      .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
      .join(' ')

    const first = coords[0]
    const last = coords[coords.length - 1]
    const area =
      first && last
        ? `${line} L${last.x.toFixed(1)} ${(PAD.top + height).toFixed(1)} L${first.x.toFixed(1)} ${(PAD.top + height).toFixed(1)} Z`
        : ''

    return { coords, line, area, top, last }
  }, [points, plotWidth, height])

  /* -- pointer → nearest index ------------------------------------- */

  const onPointerMove = React.useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      if (!model || points.length === 0) return
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX - rect.left
      // Nearest by x rather than a hit test on the line: the reader aims at a
      // moment in time, never at a 2px stroke.
      const ratio = plotWidth <= 0 ? 0 : (x - PAD.left) / plotWidth
      const index = Math.round(ratio * (points.length - 1))
      setHover(Math.min(points.length - 1, Math.max(0, index)))
    },
    [model, points.length, plotWidth]
  )

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<SVGSVGElement>) => {
      if (points.length === 0) return
      const delta = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0
      if (delta === 0) return
      event.preventDefault()
      setHover((prev) => {
        const base = prev ?? points.length - 1
        return Math.min(points.length - 1, Math.max(0, base + delta))
      })
    },
    [points.length]
  )

  const active = hover !== null ? points[hover] : undefined
  const activeCoord = hover !== null ? model?.coords[hover] : undefined

  // Hoisted: a guard on `points[points.length - 1]` does not narrow the indexed
  // access under `noUncheckedIndexedAccess`, because the index is computed.
  const firstPoint = points[0]
  const lastPoint = points[points.length - 1]

  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        {/* The title names the one series, so no legend box: a box with a
            single swatch only restates the heading and costs space. */}
        <h3 className="text-[13px] font-medium text-fg">{title}</h3>
        {model?.last && points.length > 0 ? (
          <span className="font-mono text-[12px] text-muted tabular">
            peak {formatValue(model.top)}
          </span>
        ) : null}
      </div>

      <div ref={ref} className="relative w-full">
        {model === null ? (
          <div
            className="flex items-center justify-center rounded-md border border-line bg-s0 text-[12.5px] text-faint"
            style={{ height: totalHeight }}
          >
            {emptyLabel}
          </div>
        ) : (
          <>
            <svg
              width={width}
              height={totalHeight}
              // Focusable so the crosshair is reachable without a pointer: the
              // tooltip enhances the chart, it must never be the only way to
              // read a value.
              tabIndex={0}
              role="img"
              aria-label={`${title}. Latest ${formatValue(points[points.length - 1]?.y ?? 0)}.`}
              // No focus styling of its own: theme.css carries a global
              // `:focus-visible` outline, and an `outline-none` here would be
              // the one element on the page that loses it.
              className="touch-none"
              onPointerMove={onPointerMove}
              onPointerLeave={() => {
                setHover(null)
              }}
              onKeyDown={onKeyDown}
              onBlur={() => {
                setHover(null)
              }}
            >
              {/* Gridlines: solid hairlines one step off the surface. Dashed
                  would read as a threshold rather than as a grid. */}
              {[0, 0.5, 1].map((fraction) => {
                const y = PAD.top + height * fraction
                return (
                  <g key={fraction}>
                    <line
                      x1={PAD.left}
                      x2={PAD.left + plotWidth}
                      y1={y}
                      y2={y}
                      className="stroke-line"
                      strokeWidth={1}
                    />
                    <text
                      x={PAD.left - 8}
                      y={y + 3.5}
                      textAnchor="end"
                      // Axis ticks align vertically, so tabular figures are
                      // right here — unlike the big values on the stat tiles.
                      className="fill-faint font-mono text-[10px] tabular"
                    >
                      {formatValue(model.top * (1 - fraction))}
                    </text>
                  </g>
                )
              })}

              <path d={model.area} className="fill-accent" opacity={0.1} />
              <path
                d={model.line}
                fill="none"
                className="stroke-accent"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* End marker, ringed in the surface colour so it stays legible
                  where it sits on top of the line. */}
              {model.last ? (
                <circle
                  cx={model.last.x}
                  cy={model.last.y}
                  r={4}
                  className="fill-accent stroke-s1"
                  strokeWidth={2}
                />
              ) : null}

              {/* The endpoint is the one point that gets a direct label — it is
                  the value the reader came for, and it keeps every number on
                  this chart reachable without hovering. */}
              {model.last && points.length > 0 ? (
                <text
                  x={model.last.x + 9}
                  y={model.last.y + 3.5}
                  className="fill-fg font-mono text-[11px] font-medium tabular"
                >
                  {formatValue(points[points.length - 1]?.y ?? 0)}
                </text>
              ) : null}

              {/* Crosshair */}
              {activeCoord ? (
                <g>
                  <line
                    x1={activeCoord.x}
                    x2={activeCoord.x}
                    y1={PAD.top}
                    y2={PAD.top + height}
                    className="stroke-line-strong"
                    strokeWidth={1}
                  />
                  <circle
                    cx={activeCoord.x}
                    cy={activeCoord.y}
                    r={4}
                    className="fill-accent stroke-s1"
                    strokeWidth={2}
                  />
                </g>
              ) : null}

              {/* x-axis: first and last only. Intermediate ticks on a strip
                  this wide would collide before they informed anyone. */}
              {points.length > 1 && firstPoint && lastPoint ? (
                <>
                  <text
                    x={PAD.left}
                    y={totalHeight - 5}
                    className="fill-faint font-mono text-[10px] tabular"
                  >
                    {TIME_FORMAT.format(new Date(firstPoint.x))}
                  </text>
                  <text
                    x={PAD.left + plotWidth}
                    y={totalHeight - 5}
                    textAnchor="end"
                    className="fill-faint font-mono text-[10px] tabular"
                  >
                    {TIME_FORMAT.format(new Date(lastPoint.x))}
                  </text>
                </>
              ) : null}
            </svg>

            {active && activeCoord ? (
              <div
                role="status"
                className={cn(
                  'pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full',
                  'rounded-md border border-line bg-s2 px-2 py-1 shadow-e2'
                )}
                style={{
                  left: Math.min(Math.max(activeCoord.x, 52), Math.max(52, width - 52)),
                  top: activeCoord.y - 8,
                }}
              >
                {/* Value leads, label follows: the reader already knows which
                    chart they are on and wants the number. */}
                <div className="font-mono text-[12.5px] font-semibold text-fg tabular">
                  {formatValue(active.y)}
                </div>
                <div className="font-mono text-[11px] text-muted tabular">
                  {TIME_FORMAT.format(new Date(active.x))}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
